// Menu des états des fiches dépannage et location (Jordan 30.10, lot 5), sur
// le modèle de l'atelier. Le serveur reste la référence : transitions du
// dépannage (assert_etat_transition), règle de clôture, droit
// « atelier_cloture » pour entrer en Clos, réouverture boss seul (403).
import {
	DEPANNAGE_ETAT_ANNULE,
	DEPANNAGE_ETAT_CLOS,
	DEPANNAGE_ETAT_CONTROLE_AVANT_FACTURATION,
	DEPANNAGE_ETAT_EN_ATELIER,
	DEPANNAGE_ETAT_EN_COURS,
	DEPANNAGE_ETAT_EN_ATTENTE_PAIEMENT,
	DEPANNAGE_ETAT_OK_POUR_FACTURATION,
	DEPANNAGE_ETAT_TRANSITIONS,
} from "./Addons"
import {
	LOCATION_ETAT_A_FACTURER,
	LOCATION_ETAT_ATTENTE_PAIEMENT,
	LOCATION_ETAT_CLOS,
	LOCATION_ETAT_RESTITUE,
} from "./Constants"

// → états proposés depuis l'état ENREGISTRÉ (le premier = l'état actuel).
// Clos : seulement avec le droit de clôture ; sortir de Clos : boss seul.
// Revue 30.10 (D1, Jordan) : sortir de l'état de facturation (50 → 60) =
// droit « facturer_fiche », comme le bouton « Facturer » (serveur : 403).
export const optionsEtatDepannage = (
	enregistre,
	{ isBoss, peutClore, peutFacturer = true },
) => {
	if (enregistre === DEPANNAGE_ETAT_CLOS)
		return isBoss
			? [DEPANNAGE_ETAT_CLOS, ...DEPANNAGE_ETAT_TRANSITIONS[enregistre]]
			: [DEPANNAGE_ETAT_CLOS]
	return [
		enregistre,
		...(DEPANNAGE_ETAT_TRANSITIONS[enregistre] || []),
	].filter(
		(e) =>
			(e !== DEPANNAGE_ETAT_CLOS || peutClore) &&
			!(
				enregistre === DEPANNAGE_ETAT_OK_POUR_FACTURATION &&
				e === DEPANNAGE_ETAT_EN_ATTENTE_PAIEMENT &&
				!peutFacturer
			),
	)
}

// Jordan 30.10 (lot 9) : bouton d'étape du pied de fiche, depuis l'état
// ENREGISTRÉ → { cible, libelle, autorise, titre } ou null (« Ok pour
// facturation » : bouton « Facturer » existant ; Clos / Annulé : rien).
export const etapeSuivanteDepannage = (enregistre, { peutClore }) => {
	switch (enregistre) {
		case DEPANNAGE_ETAT_EN_COURS:
		case DEPANNAGE_ETAT_EN_ATELIER:
			return {
				cible: DEPANNAGE_ETAT_CONTROLE_AVANT_FACTURATION,
				libelle: "Passer au contrôle",
				autorise: true,
			}
		case DEPANNAGE_ETAT_CONTROLE_AVANT_FACTURATION:
			return {
				cible: DEPANNAGE_ETAT_OK_POUR_FACTURATION,
				libelle: "Valider le contrôle",
				autorise: true,
			}
		case DEPANNAGE_ETAT_EN_ATTENTE_PAIEMENT:
			return {
				cible: DEPANNAGE_ETAT_CLOS,
				libelle: "Clore",
				autorise: peutClore,
				titre: peutClore
					? undefined
					: "Clore : boss, admin ou assistant(e) admin",
			}
		default:
			return null
	}
}

// Menu « ⋯ » (lot 9) : les transitions du menu du lot 5 que les boutons ne
// couvrent pas (retours en arrière, « En atelier », Annuler, réouverture
// boss) — sans l'état actuel, la cible du bouton d'étape, ni « En attente
// de paiement » ni « Clos » depuis « Ok pour facturation » (Jordan 30.10 :
// seul « Facturer » y mène ; le serveur garde 50 → 70 pour lui).
export const autresEtatsDepannage = (enregistre, droits) => {
	const etape = etapeSuivanteDepannage(enregistre, droits)
	return optionsEtatDepannage(enregistre, droits).filter(
		(e) =>
			e !== enregistre &&
			!(etape && e === etape.cible) &&
			!(
				enregistre === DEPANNAGE_ETAT_OK_POUR_FACTURATION &&
				(e === DEPANNAGE_ETAT_EN_ATTENTE_PAIEMENT ||
					e === DEPANNAGE_ETAT_CLOS)
			),
	)
}

// Libellé d'une option du menu « ⋯ ».
export const libelleAutreEtat = (enregistre, cible, libelles) =>
	cible === DEPANNAGE_ETAT_ANNULE
		? "Annuler la fiche"
		: enregistre === DEPANNAGE_ETAT_CLOS
			? `Rouvrir en « ${libelles[cible]} »`
			: `Passer en « ${libelles[cible]} »`

// Location : avant la restitution, les étapes passent par la tablette et le
// bouton « Etape suivante » (transactions créées, contrat) → pas de menu.
// `apresRestitution` : l'état qui suit « Restitué » (À facturer s'il y a une
// ligne Facture, sinon En attente de paiement — getProchainEtat).
// Revue 30.10 (D1) : « A facturer » → « En attente de paiement » = droit
// « facturer_fiche » (serveur : 403).
export const optionsEtatLocation = (
	enregistre,
	{ isBoss, peutClore, apresRestitution, peutFacturer = true },
) => {
	const suivants = {
		[LOCATION_ETAT_RESTITUE]: [apresRestitution],
		[LOCATION_ETAT_A_FACTURER]: peutFacturer
			? [LOCATION_ETAT_ATTENTE_PAIEMENT]
			: [],
		[LOCATION_ETAT_ATTENTE_PAIEMENT]: [LOCATION_ETAT_CLOS],
		[LOCATION_ETAT_CLOS]: isBoss ? [LOCATION_ETAT_ATTENTE_PAIEMENT] : [],
	}
	return [enregistre, ...(suivants[enregistre] || [])].filter(
		(e, i) =>
			i === 0 ||
			(e !== undefined && (e !== LOCATION_ETAT_CLOS || peutClore)),
	)
}

// Miroir de Depannage.assert_cloture → libellés manquants (vide = OK).
export const manquantsClotureDepannage = ({
	transactions,
	restePayer,
	isInParc,
	estFacture,
}) => {
	const txs = transactions || []
	const manquants = []
	if (txs.length === 0) manquants.push("au moins une transaction")
	else if (txs.some((t) => !t.date))
		manquants.push("la date de paiement de chaque transaction")
	if (Math.abs(parseFloat(restePayer) || 0) > 0.01)
		manquants.push("le solde des totaux (reste à payer)")
	if (isInParc) manquants.push("la sortie du véhicule du parc")
	if (txs.some((t) => estFacture(t) && !t.facture))
		manquants.push(
			"la facture d'au moins une transaction payée par facture",
		)
	return manquants
}
